import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge, Button, Card, Checkbox, Divider, Dropdown, Empty, Input,
  Modal, Select, Space, Typography, message,
} from 'antd';
import { Table, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@app/components/common/Table/Table';
import { FilterOutlined, LinkOutlined, DisconnectOutlined, ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { Card as CommonCard } from '@app/components/common/Card/Card';
// The project standardises on dayjs; DatePicker here is the dayjs-generated
// picker, not antd's moment-based one.
import { DatePicker } from '@app/components/common/pickers/DatePicker';
import { UserPicker } from '@app/components/common/UserPicker/UserPicker';
import {
  getShopifyFilterOptions,
  getShopifyOrderStats,
  getShopifyOrders,
  linkShopifyOrder,
  unlinkShopifyOrder,
} from '@app/api/admin.api';
import {
  MATCH_LABELS,
  ShopifyFilterOptions,
  ShopifyOrderModel,
  ShopifyOrderQuery,
  ShopifyOrderStats,
} from '@app/models/ShopifyOrderModel';
import {
  PageTitle, ResultCount, Toolbar, ToolbarActions,
} from '@app/components/common/PageToolbar/PageToolbar';
import {
  CellMain, CellSub, DateCell, Muted, OrderCell, Pill,
  Serial, TONE, Tile, TileGrid, TileLabel, TileValue,
} from './ShopifyOrders.styles';

const { Text } = Typography;


// ---- Filters ---------------------------------------------------------------
// Same shape as the Users page (draft vs applied, comma-joined multi-values,
// all sent server-side so they filter every page, not just the visible one).
type OrderFilters = {
  brand: string[];
  matchedBy: string[];
  hasSerial: '' | 'yes' | 'no';
  sku: string[];
  batch: string[];
  fulfillmentStatus: string[];
  dateFrom: string | null;
  dateTo: string | null;
};

const EMPTY_FILTERS: OrderFilters = {
  brand: [], matchedBy: [], hasSerial: '', sku: [], batch: [],
  fulfillmentStatus: [], dateFrom: null, dateTo: null,
};

// Orders carrying a serial are the ones that represent real hardware; the rest
// are accessory-only and rarely what support is looking for. Opening onto this
// rather than all ~9,300 rows is the difference between a tool and a dump.
const DEFAULT_FILTERS: OrderFilters = { ...EMPTY_FILTERS, hasSerial: 'yes' };

// Presets behind the summary tiles. Each is a complete filter set, so clicking
// a tile always lands on exactly what its number counts.
const TILES: { key: string; label: string; tone: string; filters: OrderFilters;
               count: (s: ShopifyOrderStats) => number }[] = [
  { key: 'serial', label: 'By serial', tone: TONE.serial,
    filters: { ...EMPTY_FILTERS, matchedBy: ['serial'] }, count: (s) => s.serial },
  { key: 'email', label: 'By email', tone: TONE.email,
    filters: { ...EMPTY_FILTERS, matchedBy: ['email'] }, count: (s) => s.email },
  { key: 'manual', label: 'Manual', tone: TONE.manual,
    filters: { ...EMPTY_FILTERS, matchedBy: ['manual'] }, count: (s) => s.manual },
  { key: 'queue', label: 'Needs linking', tone: '#ff3d71',
    filters: { ...EMPTY_FILTERS, matchedBy: ['unmatched'], hasSerial: 'yes' },
    count: (s) => s.unmatched_with_serial },
];

const sameFilters = (a: OrderFilters, b: OrderFilters) =>
  JSON.stringify(a) === JSON.stringify(b);

const buildParams = (f: OrderFilters): ShopifyOrderQuery => ({
  brand: f.brand.length ? f.brand.join(',') : undefined,
  matched_by: f.matchedBy.length ? f.matchedBy.join(',') : undefined,
  has_serial: f.hasSerial || undefined,
  sku: f.sku.length ? f.sku.join(',') : undefined,
  batch: f.batch.length ? f.batch.join(',') : undefined,
  fulfillment_status: f.fulfillmentStatus.length ? f.fulfillmentStatus.join(',') : undefined,
  date_from: f.dateFrom || undefined,
  date_to: f.dateTo || undefined,
});

const countActive = (f: OrderFilters): number =>
  (f.brand.length ? 1 : 0) + (f.matchedBy.length ? 1 : 0) + (f.hasSerial ? 1 : 0) +
  (f.sku.length ? 1 : 0) + (f.batch.length ? 1 : 0) + (f.fulfillmentStatus.length ? 1 : 0) +
  (f.dateFrom || f.dateTo ? 1 : 0);

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

// Without scroll.x antd uses auto table layout, where `width` is only a hint:
// a narrow column wraps onto a second line rather than squeezing the flexible
// Product column. Forcing nowrap makes the content its own minimum width, so
// the slack comes out of Product instead.
const noWrapTitle = (label: string) => <span style={{ whiteSpace: 'nowrap' }}>{label}</span>;

const StatTile: React.FC<{
  label: string;
  value: number;
  tone: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, value, tone, active, onClick }) => (
  <Tile type="button" $tone={tone} $active={active} onClick={onClick} title={`Show: ${label}`}>
    <TileValue $tone={tone}>{value.toLocaleString()}</TileValue>
    <TileLabel>
      {label}
    </TileLabel>
  </Tile>
);

const ShopifyOrdersPage: React.FC = () => {
  const [rows, setRows] = useState<ShopifyOrderModel[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState<OrderFilters>(DEFAULT_FILTERS);
  const [draft, setDraft] = useState<OrderFilters>(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  const [stats, setStats] = useState<ShopifyOrderStats | null>(null);
  const [options, setOptions] = useState<ShopifyFilterOptions | null>(null);

  const [linkTarget, setLinkTarget] = useState<ShopifyOrderModel | null>(null);
  const [pickedUser, setPickedUser] = useState<number | undefined>();
  const [linking, setLinking] = useState(false);

  const fetchRows = useCallback((nextPage: number, term: string, filters: OrderFilters, size = DEFAULT_PAGE_SIZE) => {
    setLoading(true);
    const params = {
      page: nextPage, page_size: size, search: term.trim() || undefined, ...buildParams(filters),
    };
    getShopifyOrders(params)
      .then((data) => {
        setRows(data.results || []);
        setCount(data.count || 0);
      })
      .catch(() => {
        setRows([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
    // Tiles reflect the whole dataset, not the current filters, so support can
    // always see overall progress while working a narrow slice.
    getShopifyOrderStats().then(setStats).catch(() => undefined);
  }, []);

  useEffect(() => {
    getShopifyFilterOptions().then(setOptions).catch(() => undefined);
    fetchRows(1, '', DEFAULT_FILTERS, DEFAULT_PAGE_SIZE);
  }, [fetchRows]);

  // Debounce the free-text search so typing doesn't fire a request per keystroke.
  const searchMounted = useRef(false);
  useEffect(() => {
    // Skip the first run: the mount effect above already did this fetch, and
    // without the guard every page load fires two identical requests.
    if (!searchMounted.current) {
      searchMounted.current = true;
      return undefined;
    }
    const t = setTimeout(() => {
      setPage(1);
      fetchRows(1, search, applied, pageSize);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const reload = () => fetchRows(page, search, applied, pageSize);

  /** Tile click: replace the whole filter set, so the table always matches
   *  the number that was clicked rather than intersecting with stale filters. */
  const applyPreset = (preset: OrderFilters) => {
    setApplied(preset);
    setDraft(preset);
    setPage(1);
    fetchRows(1, search, preset, pageSize);
  };

  const applyFilters = () => {
    setApplied(draft);
    setPage(1);
    fetchRows(1, search, draft, pageSize);
    setFilterOpen(false);
  };

  const setD = (patch: Partial<OrderFilters>) => setDraft((d) => ({ ...d, ...patch }));

  const doLink = () => {
    if (!linkTarget || !pickedUser) return;
    setLinking(true);
    linkShopifyOrder(linkTarget.id, pickedUser)
      .then(() => {
        message.success(`Order ${linkTarget.orderNumber} linked.`);
        setLinkTarget(null);
        setPickedUser(undefined);
        reload();
      })
      .catch((e) => message.error(e?.response?.data?.detail || 'Could not link that order.'))
      .finally(() => setLinking(false));
  };

  const doUnlink = (row: ShopifyOrderModel) => {
    Modal.confirm({
      title: `Unlink order ${row.orderNumber}?`,
      content: `This removes the connection to ${row.user?.email}.`,
      okText: 'Unlink',
      okButtonProps: { danger: true },
      onOk: () =>
        unlinkShopifyOrder(row.id)
          .then(() => {
            message.success('Order unlinked.');
            reload();
          })
          .catch((e) => message.error(e?.response?.data?.detail || 'Could not unlink.')),
    });
  };

  // Deliberately narrow: order, what it was, who owns it, and the action. Brand,
  // SKU and fulfilment are all still filterable, they just cost more horizontal
  // room than they earn -- SKU rides along under the product title instead.
  const columns: ColumnsType<ShopifyOrderModel> = [
    {
      title: noWrapTitle('Order'),
      dataIndex: 'orderNumber',
      width: 100,
      render: (v: string) => <OrderCell title={v}>{v || '—'}</OrderCell>,
    },
    {
      title: noWrapTitle('Product'),
      dataIndex: 'product',
      ellipsis: true,
      render: (_: string, row) => (
        <div style={{ minWidth: 0 }}>
          <CellMain title={row.product || undefined}>{row.product || '—'}</CellMain>
          <CellSub>
            {row.brand && <span style={{ textTransform: 'capitalize' }}>{row.brand}</span>}
            {row.brand && row.sku ? ' · ' : ''}
            {row.sku && `SKU ${row.sku}`}
            {row.batch ? ` · Batch ${row.batch}` : ''}
            {!row.brand && !row.sku && !row.batch ? '—' : ''}
          </CellSub>
        </div>
      ),
    },
    {
      title: noWrapTitle('Serial'),
      dataIndex: 'serialNumber',
      width: 168,
      render: (v: string) => (v ? <Serial title={v}>{v}</Serial> : <Muted>—</Muted>),
    },
    {
      title: noWrapTitle('Ordered'),
      dataIndex: 'orderedAt',
      width: 132,
      render: (v: string | null) => <DateCell>{fmtDate(v)}</DateCell>,
    },
    {
      title: noWrapTitle('Linked user'),
      width: 210,
      ellipsis: true,
      render: (_: unknown, row) =>
        row.user ? (
          <div style={{ minWidth: 0 }}>
            <CellMain title={row.user.name || undefined}>{row.user.name || 'No name'}</CellMain>
            <CellSub title={row.user.email}>{row.user.email}</CellSub>
          </div>
        ) : (
          <Muted>—</Muted>
        ),
    },
    {
      title: noWrapTitle('Matched'),
      dataIndex: 'matchedBy',
      width: 126,
      render: (_: string, row) => (
        <Pill $tone={TONE[row.matchedBy] || TONE.unmatched} title={row.matchedByLabel}>
          {MATCH_LABELS[row.matchedBy] || row.matchedByLabel}
        </Pill>
      ),
    },
    {
      title: noWrapTitle('Action'),
      width: 116,
      align: 'right',
      render: (_: unknown, row) => {
        if (row.isLocked) {
          return (
            <Muted
              style={{ fontSize: 11, whiteSpace: 'nowrap' }}
              title="Verified by cushion pairing — cannot be changed"
            >
              Locked
            </Muted>
          );
        }
        return row.user ? (
          <Button
            className="row-action"
            size="small"
            type="text"
            danger
            icon={<DisconnectOutlined />}
            title={`Unlink from ${row.user.email}`}
            onClick={() => doUnlink(row)}
          >
            Unlink
          </Button>
        ) : (
          <Button
            className="row-action"
            size="small"
            type="primary"
            ghost
            icon={<LinkOutlined />}
            onClick={() => setLinkTarget(row)}
          >
            Link
          </Button>
        );
      },
    },
  ];

  const checkboxGroup = (
    label: string,
    values: string[],
    opts: { label: string; value: string }[],
    onChange: (v: string[]) => void,
    minWidth = 150,
  ) => (
    <div style={{ minWidth }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <Checkbox.Group
        value={values}
        options={opts}
        onChange={(v) => onChange(v as string[])}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}
      />
    </div>
  );

  const filterPanel = (
    <div onClick={(e) => e.stopPropagation()}>
      <Card
        size="small"
        style={{ width: 640, maxWidth: '92vw', boxShadow: '0 6px 24px rgba(0,0,0,0.18)' }}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 28px' }}>
          {checkboxGroup(
            'Brand', draft.brand,
            (options?.brands || ['ease', 'wavon']).map((b) => ({ label: b, value: b })),
            (v) => setD({ brand: v }), 110,
          )}
          {checkboxGroup(
            'Matched by', draft.matchedBy,
            (options?.matched_by || []).map((m) => ({ label: m.label, value: m.value })),
            (v) => setD({ matchedBy: v }), 170,
          )}
          <div style={{ minWidth: 150 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Serial</div>
            <Select
              value={draft.hasSerial}
              onChange={(v) => setD({ hasSerial: v })}
              style={{ width: '100%' }}
              options={[
                { label: 'Any', value: '' },
                { label: 'Has a serial', value: 'yes' },
                { label: 'No serial', value: 'no' },
              ]}
            />
          </div>
          {checkboxGroup(
            'Fulfilment', draft.fulfillmentStatus,
            (options?.fulfillment_statuses || []).map((s) => ({ label: s, value: s })),
            (v) => setD({ fulfillmentStatus: v }),
          )}
          {checkboxGroup(
            'Batch', draft.batch,
            (options?.batches || []).map((b) => ({ label: `Batch ${b}`, value: b })),
            (v) => setD({ batch: v }), 120,
          )}
        </div>

        <Divider style={{ margin: '14px 0' }} />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px' }}>
          <div style={{ minWidth: 260 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Order date</div>
            <Space>
              <DatePicker
                value={draft.dateFrom ? dayjs(draft.dateFrom) : null}
                onChange={(d: any) => setD({ dateFrom: d ? d.format('YYYY-MM-DD') : null })}
                placeholder="From"
                getPopupContainer={(t) => t.parentElement as HTMLElement}
              />
              <DatePicker
                value={draft.dateTo ? dayjs(draft.dateTo) : null}
                onChange={(d: any) => setD({ dateTo: d ? d.format('YYYY-MM-DD') : null })}
                placeholder="To"
                getPopupContainer={(t) => t.parentElement as HTMLElement}
              />
            </Space>
          </div>
          <div style={{ minWidth: 260 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>SKU</div>
            <Select
              mode="multiple"
              allowClear
              value={draft.sku}
              onChange={(v) => setD({ sku: v })}
              style={{ width: '100%' }}
              placeholder="Any SKU"
              options={(options?.skus || []).map((s) => ({ label: s, value: s }))}
            />
          </div>
        </div>

        <Divider style={{ margin: '14px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Button type="ghost" onClick={() => setDraft(EMPTY_FILTERS)}>Clear all</Button>
            <Button type="ghost" onClick={() => setDraft(DEFAULT_FILTERS)}>Has serial</Button>
          </Space>
          <Button type="primary" onClick={applyFilters}>Apply</Button>
        </div>
      </Card>
    </div>
  );

  return (
    <>
      <Toolbar>
        <PageTitle>Shopify Orders</PageTitle>
        <ToolbarActions>
          <Input
            size="small"
            style={{ width: 340 }}
            placeholder="Search order number, serial, email, SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
          <Dropdown
            visible={filterOpen}
            onVisibleChange={(open) => {
              if (open) setDraft(applied);
              setFilterOpen(open);
            }}
            trigger={['click']}
            placement="bottomRight"
            overlay={filterPanel}
          >
            <Badge count={countActive(applied)} size="small">
              <Button size="small" icon={<FilterOutlined />}>Filters</Button>
            </Badge>
          </Dropdown>
          <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={reload}>
            Refresh
          </Button>
        </ToolbarActions>
      </Toolbar>

      {stats && (
        <TileGrid>
          {TILES.map((tile) => (
            <StatTile
              key={tile.key}
              label={tile.label}
              value={tile.count(stats)}
              tone={tile.tone}
              active={sameFilters(applied, tile.filters)}
              onClick={() => applyPreset(tile.filters)}
            />
          ))}
        </TileGrid>
      )}

      <ResultCount>
        {count.toLocaleString()} orders · page {page}
      </ResultCount>

      <CommonCard id="shopify-orders-table" padding="0.5rem 0.75rem 0">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          locale={{ emptyText: <Empty description="No orders match these filters" /> }}
          pagination={{
            current: page,
            pageSize,
            total: count,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            // Server-side: changing either page or size has to refetch, not
            // just re-slice the rows already loaded.
            onChange: (p, size) => {
              const nextSize = size || pageSize;
              setPage(p);
              setPageSize(nextSize);
              fetchRows(p, search, applied, nextSize);
            },
          }}
        />
      </CommonCard>

      <Modal
        title={`Link order ${linkTarget?.orderNumber || ''}`}
        visible={!!linkTarget}
        onCancel={() => {
          setLinkTarget(null);
          setPickedUser(undefined);
        }}
        onOk={doLink}
        okText="Link"
        okButtonProps={{ disabled: !pickedUser }}
        confirmLoading={linking}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {linkTarget?.serialNumber && (
            <Text type="secondary">
              Serial <Text code>{linkTarget.serialNumber}</Text>
              {linkTarget.email ? ` · ordered by ${linkTarget.email}` : ''}
            </Text>
          )}
          <div>
            <div style={{ marginBottom: 4 }}>Link to user</div>
            <UserPicker value={pickedUser} onChange={setPickedUser} />
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Manual links are never overwritten by the automatic sync.
          </Text>
        </Space>
      </Modal>
    </>
  );
};

export default ShopifyOrdersPage;
